import React, { useCallback, useMemo } from "react";
import classNames from "classnames";
import { Field, useForm } from "react-final-form";
import { useSelector } from "react-redux";
import { ParameterFields } from "../../../../types";
import TextTruncate from "components/TextTruncate";
import NumberInput from "rs-components/Input/components/NumberInput";
import Input from "rs-components/Input";
import SelectInput from "rs-components/Input/components/SelectInput";
import SelectItemRenderer from "rs-components/Input/components/SelectInput/components/SelectItemRenderer";
import RangeValue from "rs-components/RangeValue";
import ParameterValues from "../../../../../ParameterValues";
import { findDictionaryItemByValue, resultElementParameterType } from "shared/rsDictionary";
import { ResultElementParameterType, } from "store/rs-workspace/rs-workspace.types";
import { parameterTypesSelector } from "store/global/global.selectors";
import { isNotEmpty } from "utils/validate";
import { convertValueBasedOnUnits } from "utils/final-form-mutators";
import { RequestTemplate } from "store/knowledge-base-item/knowledge-base-item.types";
import { getErrorByPropertyName } from "store/knowledge-base-item/knowledge-base-item.utils";
import { serverErrorsSelector } from "store/knowledge-base-item/knowledge-base-item.selectors";
import { NoUnitsType } from "store/recipe/recipe.types";
import { getRecipeParameterUnitsItems } from "store/rs-workspace/utils/results.utils";
import * as styles from "../../../../styles.module.scss";
const CellModelOrRecipeParameterFields = ({ name, parameter, sources, restrictions }) => {
    const form = useForm();
    const parameterTypes = useSelector(parameterTypesSelector);
    const serverErrors = useSelector(serverErrorsSelector(RequestTemplate.SPACEVIEWER_ELEMENT_PUT));
    const unitsAreReadonly = useMemo(() => {
        const { type, units } = { ...parameter.parameter };
        const hasNoUnits = units === NoUnitsType.NoUnits;
        const isCellModelParameter = type === ResultElementParameterType.CELL_MODEL;
        return isCellModelParameter || hasNoUnits;
    }, [parameter]);
    const unitsOptions = useMemo(() => {
        const { name, units } = { ...parameter.parameter };
        if (!parameter || !units) {
            return [];
        }
        return getRecipeParameterUnitsItems(name, units, parameterTypes, [], true).map((item) => !isNotEmpty(item.value, true) ? { ...item, className: styles["field-select-groupTitle"] } : item);
    }, [parameter, parameterTypes]);
    const units = useMemo(() => unitsOptions.find(({ value }) => value === parameter.parameter.units)?.label || "", [parameter.parameter, unitsOptions]);
    const handleChangeUnits = useCallback((value) => {
        const prevUnits = form.getFieldState(`${name}.${ParameterFields.parameter}.${ParameterFields.units}`)?.value ??
            undefined;
        const newMinValue = convertValueBasedOnUnits(form.getFieldState(`${name}.${[ParameterFields.parameter]}.${ParameterFields.min}`)?.value ??
            undefined, prevUnits, value);
        const newMaxValue = convertValueBasedOnUnits(form.getFieldState(`${name}.${[ParameterFields.parameter]}.${ParameterFields.max}`)?.value ??
            undefined, prevUnits, value);
        form.batch(() => {
            form.change(`${name}.${ParameterFields.parameter}.${ParameterFields.units}`, value);
            form.change(`${name}.${[ParameterFields.parameter]}.${ParameterFields.min}`, newMinValue);
            form.change(`${name}.${[ParameterFields.parameter]}.${ParameterFields.max}`, newMaxValue);
        });
    }, [form, name]);
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: classNames(styles.cell, styles.type) },
            React.createElement(Field, { name: `${name}.${ParameterFields.type}`, component: "input", type: "hidden" }),
            React.createElement(TextTruncate, { text: findDictionaryItemByValue(resultElementParameterType, parameter?.parameter?.type)?.label.replace(" parameter", ""), className: styles["preview-value"] })),
        React.createElement("div", { className: classNames(styles.cell, styles.value) },
            React.createElement(ParameterValues, { parameter: parameter, sources: sources })),
        React.createElement("div", { className: classNames(styles.cell, styles.unit) }, unitsAreReadonly ? (React.createElement(TextTruncate, { className: styles.readonlyUnit, text: units })) : (React.createElement(Input, { name: `${name}.${ParameterFields.parameter}.${ParameterFields.units}`, component: SelectInput, items: unitsOptions, placeholder: "Select", itemRenderer: SelectItemRenderer, classes: {
                container: styles.selectWrapper,
                component: classNames(styles.select, {
                    [styles.empty]: !isNotEmpty(parameter?.[ParameterFields.parameter]),
                    [styles.readonly]: unitsAreReadonly,
                }),
            }, disableFormChange: true, onChange: handleChangeUnits, valueFormatter: ({ value }) => value, restrictions: restrictions?.Parameter?.Parameter?.Units, externalError: getErrorByPropertyName(`Request.${name}.${ParameterFields.parameter}.${ParameterFields.units}`, serverErrors)?.text, disabled: form.getState().invalid }))),
        React.createElement(RangeValue, { from: {
                component: NumberInput,
                name: `${name}.${[ParameterFields.parameter]}.${ParameterFields.min}`,
                disabled: !isNotEmpty(parameter?.[ParameterFields.parameter]),
                classes: {
                    component: classNames(styles["number-input"], {
                        [styles.empty]: !isNotEmpty(parameter?.[ParameterFields.parameter]),
                    }),
                },
                restrictions: { ...restrictions?.Min, IsNumber: true },
                externalError: getErrorByPropertyName(`Request.${name}.${[ParameterFields.parameter]}.${ParameterFields.min}`, serverErrors)?.text,
            }, to: {
                component: NumberInput,
                name: `${name}.${[ParameterFields.parameter]}.${ParameterFields.max}`,
                disabled: !isNotEmpty(parameter?.[ParameterFields.parameter]),
                classes: {
                    component: classNames(styles["number-input"], {
                        [styles.empty]: !isNotEmpty(parameter?.[ParameterFields.parameter]),
                    }),
                },
                restrictions: { ...restrictions?.Max, IsNumber: true },
                externalError: getErrorByPropertyName(`Request.${name}.${[ParameterFields.parameter]}.${ParameterFields.max}`, serverErrors)?.text,
            }, suppressDash: true, className: styles.cell, editable: true }),
        React.createElement("div", { className: classNames(styles.cell, styles.granularity) },
            React.createElement(Input, { name: `${name}.${[ParameterFields.parameter]}.${ParameterFields.granularity}`, component: NumberInput, errorInTooltip: true, disabled: !isNotEmpty(parameter?.[ParameterFields.parameter]), restrictions: { ...restrictions?.Granularity, IsNumber: true, IsInteger: true }, classes: {
                    component: classNames(styles["number-input"], {
                        [styles.empty]: !isNotEmpty(parameter?.[ParameterFields.parameter]),
                    }),
                }, externalError: getErrorByPropertyName(`Request.${name}.${[ParameterFields.parameter]}.${ParameterFields.granularity}`, serverErrors)?.text }))));
};
export default CellModelOrRecipeParameterFields;
